import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import FilterIcon from '../../assets/icons/filter-icon.svg?react';
import SearchInput from '../../components/SearchInput';
import Select from '../../components/Select';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import { useNavigate } from 'react-router';
import Pagination from '../../components/CustomPagination';
import HammerActiveIcon from '../../assets/icons/hammer-active-icon.svg?react';
import DownloadIcon from '../../assets/icons/download-icon.svg?react';
import {
  GetGetIncidentsInjuriesResponse,
  Incidents,
  IncidentsInvestigationStatusEnum,
  IncidentsSeverityEnum,
  IncidentsStatusEnum,
  InvestigationReportingApiInvestigationReportingControllerFindAllInjuriesIncidentRequest,
  InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentStatusEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
} from '@EHSNavigator/backend';
import { useDebounce } from '@uidotdev/usehooks';
import FilterClearIcon from '../../assets/icons/filter-clear-icon.svg?react';
import NoItemsFound from '../../components/NoItemsFound';
import Loader from '../../components/Loader';
import Button from '../../components/Button';
import ReportFilledIcon from '../../assets/icons/report-filled-icon.svg?react';
import { useClickOutside } from '../../utils/clickOutSide';
import {
  handleExportToCSV,
  handleInjuriesExportToExcel,
} from '../../utils/exportTable';

interface InjuriesHubProps {
  otherClasses?: string;
  params: InvestigationReportingApiInvestigationReportingControllerFindAllInjuriesIncidentRequest;
  departments: {
    label: string;
    value: string;
  }[];

  data: GetGetIncidentsInjuriesResponse;
  noDataMessage: string;
  isIncidentsLoading: boolean;
}

const InjuriesHub: React.FC<InjuriesHubProps> = ({
  otherClasses,
  params,
  departments,
  data,
  noDataMessage,
  isIncidentsLoading,
}) => {
  const myReportedIncidentsClasses = clsx(
    otherClasses,
    'flex flex-col w-full flex-1',
  );
  const navigate = useNavigate();
  const [toggle, setToggle] = useState(false);
  const ref = useRef(null);
  useClickOutside({ ref, setToggle });
  const [state, setState] = useState({
    page: Number(params?.page),
    search: params?.search || '',
    type: params?.type || '',
    classification: params?.classification || '',
    typeOfLoss: params?.typeOfLoss || '',
    department: params?.departmentId || '',
    status: params?.status || '',
    severity: params?.severity || '',
    limit: params?.limit || 10,
  });
  const debouncedSearch = useDebounce(state?.search, 600);
  const dispatch = useDispatch();
  const prevFiltersRef = useRef({});
  const isFirstRun = useRef(true);

  useEffect(() => {
    const filters = {
      search: debouncedSearch,
      type: state.type,
      classification: state.classification,
      typeOfLoss: state.typeOfLoss,
      department: state.department,
      status: state.status,
      severity: state.severity,
      limit: state.limit,
    };
    let shouldResetPage = false;
    if (!isFirstRun.current) {
      const filtersChanged =
        JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
      if (filtersChanged) {
        shouldResetPage = true;
        setState((prev) => ({ ...prev, page: 1 }));
      }
    }
    const paramsSearch = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'limit' && Number(value) === 10) {
        return;
      }
      if (value) paramsSearch.set(key, value.toString());
    });
    if (state.page !== 1) {
      paramsSearch.set('page', state.page.toString());
    }
    if (shouldResetPage) {
      paramsSearch.delete('page');
      setState((prev) => ({ ...prev, page: 1 }));
    }
    prevFiltersRef.current = filters;
    if (!isFirstRun.current) {
      navigate(`/injuries?${paramsSearch.toString()}`);
    }
    isFirstRun.current = false;
  }, [
    debouncedSearch,
    state.type,
    state.classification,
    state.typeOfLoss,
    state.department,
    state.status,
    state.page,
    state.severity,
    state.limit,
  ]);

  const updateQuery = (queryName: string, queryValue: string) => {
    const params = new URLSearchParams(window.location.search);
    if (queryName === 'page' && queryValue === '1') {
      params.delete('page');
    } else {
      params.set(queryName, queryValue);
    }

    navigate(`/injuries?${params.toString()}`);
  };

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    updateQuery('page', String(newPage));
  };

  const exportData = {
    header: [
      'Sr.',
      'Ref#',
      'Title',
      'Employee Affected',
      'Severity',
      'Investigation Status',
      'Corrective Action Status',
    ],
    columnWidths: [
      { width: 5 },
      { width: 16 },
      { width: 30 },
      { width: 25 },
      { width: 10 },
      { width: 22 },
      { width: 27 },
    ],
    exportData: data?.incidents?.map((incident: any, index: number) => {
      return {
        'Sr.': (state.page - 1) * state.limit + index + 1,
        'Ref#': incident.incidentRefId,
        Title: incident.title
          ? incident.title
              .split(' ')
              .map(
                (word: string) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
              )
              .join(' ')
          : '',
        'Employee Affected': incident?.user?.fullName,
        Severity: incident.severity,
        'Investigation Status': !(
          incident?.investigationStatus === 'Pending' ||
          !incident?.investigationStatus
        )
          ? 'Done'
          : 'Pending',
        'Corrective Action Status':
          incident?.status === IncidentsStatusEnum.Active &&
          incident?.user?.role?.name !== 'Employee'
            ? 'Unassigned'
            : incident?.correctiveActions?.length === 0
              ? 'Unassigned'
              : 'Assigned',
      };
    }),
  };

  const options = [
    {
      label: 'Export CSV',
      onClick: () => {
        handleExportToCSV(
          exportData?.exportData,
          exportData?.header,
          'injuries',
        );
      },
    },
    {
      label: 'Export XLSX',
      onClick: () => {
        handleInjuriesExportToExcel(
          exportData?.exportData,
          exportData?.header,
          exportData?.columnWidths,
        );
      },
    },
  ];

  return (
    <div
      className={myReportedIncidentsClasses}
      data-testid="my-reported-incidents"
    >
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div>
          <h1 className="h3">Reported Injuries/Illnesses</h1>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
            Review all incidents reported that were classified under the
            injuries and illnesses category
          </p>
          {data?.closedIncidentCount !== 0 && (
            <p className="font-inter-variable text-brand-posh-pink mt-15 hidden items-center gap-1 text-xl leading-6 font-bold lg:flex">
              <WarningIcon /> {data?.closedIncidentCount} Investigation(s) are
              disabled because their linking incident(s) have been closed.
            </p>
          )}
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 text-lg leading-5 font-medium lg:hidden">
        Review all incidents reported that were classified under the injuries
        and illnesses category
      </p>

      <div className="global-scrollbar-style flex-1 overflow-auto px-0.5">
        <div className="flex w-full min-w-6xl flex-col">
          <hr className="border-neutral-tones-text/50 mt-9 mb-6 lg:hidden" />
          <div className="flex items-stretch gap-2.5 lg:mt-12">
            <SearchInput
              name="search"
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  search: e?.target?.value,
                }))
              }
              placeholder="Enter title, Id, employee name, or department ."
              value={state?.search}
              disabled={false}
              errorMessage=""
              otherClasses={{ container: 'w-full ' }}
            />
            <div ref={ref} className="relative flex lg:block">
              <Button
                onClick={() => setToggle(!toggle)}
                otherClasses="!py-0 flex-1 h-full"
                size="smaller"
                disable={data?.incidents?.length === 0}
              >
                <DownloadIcon className="rotate-180 [&>g>path]:fill-current" />{' '}
                <span className="hidden md:block">Export</span>
              </Button>
              {toggle && (
                <ul
                  className={clsx(
                    'bg-light-translucent-background ring-neutral-tones-text/35 dark:bg-dark-translucent-background absolute top-14 right-0 z-10 flex w-fit flex-col rounded-md p-2.5 ring md:w-full lg:top-18',
                  )}
                >
                  {options.map((option, index) => (
                    <button
                      key={index}
                      className={clsx(
                        'font-inter-variable text-neutral-tones-text border-b-neutral-tones-text/35 hover:text-brand-byzantine global-transition cursor-pointer border-b py-2 text-left text-sm font-normal whitespace-nowrap first:pt-0 last:border-b-0 last:pb-0 disabled:cursor-not-allowed disabled:text-neutral-600',
                      )}
                      onClick={() => option.onClick()}
                    >
                      {option.label}
                    </button>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="border-b-neutral-tones-text/50 relative mt-6 flex flex-col gap-4 border-b pb-6 lg:mb-12 lg:flex-row lg:items-center lg:gap-9">
            <p className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg">
              <button
                onClick={() => {
                  if (
                    state?.search !== '' ||
                    state?.type !== '' ||
                    state?.classification !== '' ||
                    state?.typeOfLoss !== '' ||
                    state?.department !== '' ||
                    state?.status !== '' ||
                    state?.severity !== '' ||
                    state?.limit !== 10
                  ) {
                    setState((prev) => ({
                      ...prev,
                      search: '',
                      type: '',
                      classification: '',
                      typeOfLoss: '',
                      department: '',
                      status: '',
                      severity: '',
                      limit: 10,
                    }));
                  }
                }}
                className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex cursor-pointer items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg"
              >
                {state?.search !== '' ||
                state?.type !== '' ||
                state?.classification !== '' ||
                state?.typeOfLoss !== '' ||
                state?.department !== '' ||
                state?.severity !== '' ||
                state?.status !== '' ||
                state?.limit !== 10 ? (
                  <FilterClearIcon className="[&>path]:fill-brand-byzantine h-6 w-6 lg:h-9 lg:w-9" />
                ) : (
                  <FilterIcon className="h-6 w-6 lg:h-9 lg:w-9" />
                )}
                Filters
              </button>
            </p>
            <div className="flex flex-1 items-center gap-6">
              <Select
                filterSelect
                list={Object.values(
                  InvestigationReportingControllerFindAllInjuriesIncidentTypeEnum,
                ).map((type) => ({
                  label: type,
                  value: type,
                }))}
                value={state?.type}
                placeholder="Type"
                otherClasses={{
                  container: 'w-full max-w-34',
                  listContainer:
                    'top-auto bottom-14 !max-h-40 lg:bottom-auto lg:top-16',
                }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    type: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={Object.values(
                  InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum,
                ).map((classification) => ({
                  label: classification,
                  value: classification,
                }))}
                value={state?.classification}
                placeholder="Classification"
                otherClasses={{
                  container: 'w-full max-w-48',
                  listContainer:
                    'top-auto bottom-14 !max-h-40 lg:bottom-auto lg:top-16',
                }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    classification: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={Object.values(
                  InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
                ).map((typeOfLoss) => ({
                  label: typeOfLoss,
                  value: typeOfLoss,
                }))}
                value={state?.typeOfLoss}
                placeholder="Type of Loss"
                otherClasses={{
                  container: 'w-full max-w-46',
                  listContainer:
                    'top-auto bottom-14 !max-h-40 lg:bottom-auto lg:top-16',
                }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    typeOfLoss: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={departments}
                value={state?.department}
                placeholder="Departments"
                otherClasses={{
                  container: 'w-full max-w-44',
                  listContainer:
                    'top-auto bottom-14 !max-h-40 lg:bottom-auto lg:top-16',
                }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    department: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={Object.values(
                  InvestigationReportingControllerFindAllInjuriesIncidentStatusEnum,
                ).map((status) => ({
                  label: status,
                  value: status,
                }))}
                value={state?.status}
                placeholder="Status"
                otherClasses={{
                  container: 'w-full max-w-36',
                  listContainer:
                    'top-auto bottom-14 !max-h-40 lg:bottom-auto lg:top-16',
                }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    status: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={Object.entries(IncidentsSeverityEnum).map(
                  ([, value]) => ({
                    label: value,
                    value,
                  }),
                )}
                value={state?.severity}
                placeholder="Severities"
                otherClasses={{
                  container: 'w-full max-w-36',
                  listContainer:
                    'top-auto bottom-14 !max-h-40 lg:bottom-auto lg:top-16',
                }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    severity: e,
                  }))
                }
                errorMessage=""
              />
            </div>
          </div>
        </div>

        <div className="hidden lg:block">
          {isIncidentsLoading ? (
            <div>
              <Loader
                variant="compact"
                otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
              />
            </div>
          ) : (
            <div className="w-full lg:min-w-6xl">
              <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
                <h6
                  className={clsx(
                    'text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-2 pr-6 font-bold lg:col-span-1 lg:text-lg lg:leading-5',
                    data?.closedIncidentCount !== 0
                      ? 'text-right'
                      : 'text-left',
                  )}
                >
                  Sr.
                </h6>
                <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-2 hidden font-bold lg:block lg:text-lg lg:leading-5">
                  Ref#
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-6 font-bold lg:col-span-3 lg:text-lg lg:leading-5">
                  Title
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-3 hidden font-bold lg:block lg:text-lg lg:leading-5">
                  Employee Affected
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-4 font-bold lg:col-span-3 lg:text-lg lg:leading-5">
                  Severity
                </h6>
              </div>

              {data?.incidents?.length > 0 ? (
                <>
                  {data?.incidents?.map(
                    (incident: Incidents, index: number) => {
                      return (
                        <div
                          key={index}
                          className="border-b-neutral-tones-text/50 grid grid-cols-12 items-center gap-4 border-b py-5"
                        >
                          <p
                            onClick={(e) => e.preventDefault()}
                            className={clsx(
                              'text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center pr-6 text-sm leading-4 font-bold lg:col-span-1 lg:text-base lg:leading-5',
                              incident.status === IncidentsStatusEnum.Closed &&
                                (incident?.investigationStatus === null ||
                                  incident?.investigationStatus ===
                                    IncidentsInvestigationStatusEnum.Pending)
                                ? 'justify-between'
                                : data?.closedIncidentCount === 0
                                  ? 'justify-start'
                                  : 'justify-end',
                            )}
                          >
                            {incident.status === IncidentsStatusEnum.Closed &&
                              (incident?.investigationStatus === null ||
                                incident?.investigationStatus ===
                                  IncidentsInvestigationStatusEnum.Pending) && (
                                <button className="group relative">
                                  <WarningIcon className="h-5 min-h-5 w-5 min-w-5" />
                                  <p className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-6 left-0 z-10 hidden min-w-36 items-center justify-center rounded px-2 py-1 text-xs font-normal shadow-lg group-hover:block group-focus:block">
                                    This investigation has been disabled due to
                                    closed linking incident.
                                  </p>
                                </button>
                              )}
                            {(state.page - 1) * state.limit + index + 1}
                          </p>
                          <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-2 hidden text-sm leading-4 font-normal uppercase first-letter:uppercase lg:block lg:text-base lg:leading-5">
                            {incident.incidentRefId}
                          </p>
                          <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-6 text-sm leading-4 font-normal lowercase first-letter:uppercase lg:col-span-3 lg:text-base lg:leading-5">
                            {incident.title}
                          </p>
                          <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-3 hidden text-sm leading-4 font-normal lg:block lg:text-base lg:leading-5">
                            {incident?.user?.fullName}
                          </p>
                          <div className="col-span-4 flex items-center justify-start lg:col-span-3 lg:justify-between">
                            <p
                              className={clsx(
                                'font-inter-variable lg:-tracking-form-input hidden text-sm leading-4 font-bold lg:block lg:text-base lg:leading-5',
                                incident.severity ===
                                  IncidentsSeverityEnum.Critical
                                  ? 'text-brand-posh-pink'
                                  : incident.severity ===
                                      IncidentsSeverityEnum.Major
                                    ? 'text-brand-fair-orange'
                                    : incident.severity ===
                                        IncidentsSeverityEnum.Moderate
                                      ? 'text-primary-yellow'
                                      : 'text-brand-papery',
                              )}
                            >
                              {incident.severity}
                            </p>
                            <div className="flex items-center gap-2.5 lg:gap-15">
                              <Button
                                size="smaller"
                                href={
                                  incident?.investigationStatus === 'Pending' ||
                                  !incident?.investigationStatus
                                    ? '/injuries/create-investigation/' +
                                      incident.id
                                    : '/injuries/' +
                                      incident?.investigation?.[0]?.id
                                }
                                otherClasses={clsx(
                                  'w-32 ',
                                  incident?.status ===
                                    IncidentsStatusEnum.Closed &&
                                    incident?.investigationStatus !==
                                      IncidentsInvestigationStatusEnum.Done &&
                                    '!bg-additional-disable text-neutral-tones-text/50 hover:border-transparent hover:bg-additional-disable pointer-events-none hover:!text-neutral-tones-text/50',
                                  !(
                                    incident?.investigationStatus ===
                                      'Pending' ||
                                    !incident?.investigationStatus
                                  )
                                    ? 'bg-brand-papery hover:text-brand-papery hover:border-brand-papery'
                                    : '',
                                )}
                              >
                                <span className="hidden lg:block">
                                  {!(
                                    incident?.investigationStatus ===
                                      'Pending' ||
                                    !incident?.investigationStatus
                                  )
                                    ? 'View Report'
                                    : 'Investigate'}
                                </span>
                                <ReportFilledIcon className="h-5 w-5 lg:hidden [&>path]:fill-current" />
                              </Button>

                              <Button
                                href={
                                  incident?.status ===
                                    IncidentsStatusEnum.Active &&
                                  incident?.user?.role?.name !== 'Employee'
                                    ? '/incident-review/' + incident.id
                                    : incident?.correctiveActions?.length === 0
                                      ? '/incident-review/' + incident.id
                                      : '/all-corrective-actions/' +
                                        incident?.correctiveActions?.[0]?.id
                                }
                                size="smaller"
                                variant={
                                  incident?.status ===
                                    IncidentsStatusEnum.Active &&
                                  incident?.user?.role?.name !== 'Employee'
                                    ? 'secondary'
                                    : incident?.correctiveActions?.length === 0
                                      ? 'secondary'
                                      : 'primary'
                                }
                                otherClasses={clsx(
                                  '',
                                  incident?.status ===
                                    IncidentsStatusEnum.Active &&
                                    incident?.user?.role?.name !== 'Employee'
                                    ? 'text-brand-byzantine'
                                    : incident?.correctiveActions?.length === 0
                                      ? 'text-brand-byzantine'
                                      : 'bg-brand-papery hover:!text-brand-papery hover:!border-brand-papery',
                                )}
                              >
                                <HammerActiveIcon className="h-5 w-5 [&>path]:fill-current" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    },
                  )}
                </>
              ) : (
                <NoItemsFound message={noDataMessage} />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 lg:hidden">
        {isIncidentsLoading ? (
          <div>
            <Loader
              variant="compact"
              otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
            />
          </div>
        ) : (
          <div className="w-full">
            <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
              <h6
                className={clsx(
                  'text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold sm:pr-6 lg:col-span-1 lg:text-lg lg:leading-5',
                  data?.closedIncidentCount !== 0 ? 'text-right' : 'text-left',
                )}
              >
                Sr.
              </h6>
              <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-2 hidden font-bold lg:block lg:text-lg lg:leading-5">
                Ref#
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-6 font-bold lg:col-span-3 lg:text-lg lg:leading-5">
                Injury/Illness
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-3 hidden font-bold lg:block lg:text-lg lg:leading-5">
                Employee Affected
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-4 hidden font-bold md:block lg:col-span-3 lg:text-lg lg:leading-5">
                Severity
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-4 font-bold md:hidden lg:col-span-3 lg:text-lg lg:leading-5">
                Actions
              </h6>
            </div>

            {data?.incidents?.length > 0 ? (
              <>
                {data?.incidents?.map((incident: Incidents, index: number) => {
                  return (
                    <div
                      key={index}
                      className="border-b-neutral-tones-text/50 grid grid-cols-12 items-center gap-4 border-b py-5"
                    >
                      <p
                        className={clsx(
                          'text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-bold sm:pr-6 lg:col-span-1 lg:text-base lg:leading-5',
                          incident.status === IncidentsStatusEnum.Closed &&
                            (incident?.investigationStatus === null ||
                              incident?.investigationStatus ===
                                IncidentsInvestigationStatusEnum.Pending)
                            ? 'justify-between'
                            : data?.closedIncidentCount === 0
                              ? 'justify-start'
                              : 'justify-end',
                        )}
                      >
                        {incident.status === IncidentsStatusEnum.Closed &&
                          (incident?.investigationStatus === null ||
                            incident?.investigationStatus ===
                              IncidentsInvestigationStatusEnum.Pending) && (
                            <button className="group relative">
                              <WarningIcon className="h-5 min-h-5 w-5 min-w-5" />
                              <span className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-6 left-0 z-10 hidden min-w-36 items-center justify-center rounded px-2 py-1 text-xs font-normal shadow-lg group-hover:block group-focus:block">
                                This investigation has been disabled due to
                                closed linking incident.
                              </span>
                            </button>
                          )}
                        {(state.page - 1) * state.limit + index + 1}
                      </p>
                      <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-2 hidden text-sm leading-4 font-normal uppercase first-letter:uppercase lg:block lg:text-base lg:leading-5">
                        {incident.incidentRefId}
                      </p>
                      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-6 text-sm leading-4 font-normal lowercase first-letter:uppercase lg:col-span-3 lg:text-base lg:leading-5">
                        {incident.title}
                      </p>
                      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-3 hidden text-sm leading-4 font-normal lg:block lg:text-base lg:leading-5">
                        {incident?.user?.fullName}
                      </p>
                      <div className="col-span-4 flex items-center justify-start md:justify-between lg:col-span-3">
                        <p
                          className={clsx(
                            'font-inter-variable lg:-tracking-form-input hidden text-sm leading-4 font-bold md:block lg:block lg:text-base lg:leading-5',
                            incident.severity === IncidentsSeverityEnum.Critical
                              ? 'text-brand-posh-pink'
                              : incident.severity ===
                                  IncidentsSeverityEnum.Major
                                ? 'text-brand-fair-orange'
                                : incident.severity ===
                                    IncidentsSeverityEnum.Moderate
                                  ? 'text-primary-yellow'
                                  : 'text-brand-papery',
                          )}
                        >
                          {incident.severity}
                        </p>
                        <div className="flex items-center gap-2.5 lg:gap-15">
                          <Button
                            href={
                              incident?.investigationStatus === 'Pending' ||
                              !incident?.investigationStatus
                                ? '/injuries/create-investigation/' +
                                  incident.id
                                : '/injuries/' +
                                  incident?.investigation?.[0]?.id
                            }
                            otherClasses={clsx(
                              'w-28 !px-0 !text-base/5 !py-2 md:flex hidden justify-center',
                              incident?.status === IncidentsStatusEnum.Closed &&
                                incident?.investigationStatus !==
                                  IncidentsInvestigationStatusEnum.Done &&
                                '!bg-additional-disable text-neutral-tones-text/50 hover:border-transparent hover:bg-additional-disable pointer-events-none hover:!text-neutral-tones-text/50',
                              !(
                                incident?.investigationStatus === 'Pending' ||
                                !incident?.investigationStatus
                              )
                                ? 'bg-brand-papery hover:text-brand-papery hover:border-brand-papery'
                                : '',
                            )}
                          >
                            {!(
                              incident?.investigationStatus === 'Pending' ||
                              !incident?.investigationStatus
                            )
                              ? 'View Report'
                              : 'Investigate'}
                          </Button>
                          <Button
                            size="smaller"
                            href={
                              incident?.investigationStatus === 'Pending' ||
                              !incident?.investigationStatus
                                ? '/injuries/create-investigation/' +
                                  incident.id
                                : '/injuries/' +
                                  incident?.investigation?.[0]?.id
                            }
                            otherClasses={clsx(
                              'md:hidden',
                              incident?.status === IncidentsStatusEnum.Closed &&
                                incident?.investigationStatus !==
                                  IncidentsInvestigationStatusEnum.Done &&
                                '!bg-additional-disable text-neutral-tones-text/50 hover:border-transparent hover:bg-additional-disable pointer-events-none hover:!text-neutral-tones-text/50',
                              !(
                                incident?.investigationStatus === 'Pending' ||
                                !incident?.investigationStatus
                              )
                                ? 'bg-brand-papery hover:text-brand-papery hover:border-brand-papery'
                                : '',
                            )}
                          >
                            <ReportFilledIcon className="h-5 w-5 lg:hidden [&>path]:fill-current" />
                          </Button>
                          <Button
                            href={
                              incident?.status === IncidentsStatusEnum.Active &&
                              incident?.user?.role?.name !== 'Employee'
                                ? '/incident-review/' + incident.id
                                : incident?.correctiveActions?.length === 0
                                  ? '/incident-review/' + incident.id
                                  : '/all-corrective-actions/' +
                                    incident?.correctiveActions?.[0]?.id
                            }
                            size="smaller"
                            variant={
                              incident?.status === IncidentsStatusEnum.Active &&
                              incident?.user?.role?.name !== 'Employee'
                                ? 'secondary'
                                : incident?.correctiveActions?.length === 0
                                  ? 'secondary'
                                  : 'primary'
                            }
                            otherClasses={clsx(
                              '',
                              incident?.status === IncidentsStatusEnum.Active &&
                                incident?.user?.role?.name !== 'Employee'
                                ? 'text-brand-byzantine'
                                : incident?.correctiveActions?.length === 0
                                  ? 'text-brand-byzantine'
                                  : 'bg-brand-papery hover:!text-brand-papery hover:!border-brand-papery',
                            )}
                          >
                            <HammerActiveIcon className="h-5 w-5 [&>path]:fill-current" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <NoItemsFound message={noDataMessage} />
            )}
          </div>
        )}
      </div>
      {data?.incidents?.length > 0 && (
        <Pagination
          currentPage={state.page}
          totalPages={data?.meta?.totalPages}
          totalItems={data?.meta?.total}
          limit={data?.meta?.limit}
          showingItems={data?.incidents?.length}
          onPageChange={handlePageChange}
          onLimitChange={(e) =>
            setState((prev) => ({ ...prev, limit: e, page: 1 }))
          }
          otherClasses=""
        />
      )}
    </div>
  );
};

export default InjuriesHub;
